// apps/client/src/pages/UploadPage.tsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAppContext } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { brokerFilteredAdminProps } from '../utils/brokerFilteredAdmin';
import { useTrackEvent } from '../hooks/useTrackEvent';
import { db, auth, functions } from '../lib/firebase';
// This line is required for the delete function
import { httpsCallable } from 'firebase/functions';
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    Timestamp,
    where,
} from 'firebase/firestore';
import { organizeFilesByOfficeTemplateYearMonth } from '../utils/dataProcessing';
import { CloudUpload, Trash2, Upload, ChevronRight, ChevronDown } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { Template, SourceFile } from '../types';
// This line is required for the new upload function
import { getStorage, ref, uploadBytes } from "firebase/storage";
// Multi-Entity Upload (Premium Feature)
import { useFeatureEntitlement } from '../hooks/useFeatureEntitlement';
import MultiEntityUploader from '../components/MultiEntityUploader';
import UpgradePrompt from '../components/UpgradePrompt';
import { formatUploadDisplayName } from '@valutten/shared-utils';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection, resolveUploadSeedSelection } from '../utils/loanBookResolution';
import { deriveUploadProcessingBanner, uploadBannerClass } from '../utils/uploadProcessingBanner';
import { resolveUploadRowTitles, type UploadRowFile } from '../utils/uploadRowTitle';

interface ProcessedFileResult {
  recordCount: number;
  success: boolean;
}

const UploadPage: React.FC = () => {
    const { userData, companyData, userVisibleOffices: offices, userVisibleTemplates, loanBooks = [], loanBooksLoading } = useAppContext();
    // A broker-restricted admin cannot ingest or remove source documents: the
    // callables default-deny her at the auth chokepoint, so the controls are
    // disabled and labelled rather than left to fail server-side.
    const { isBrokerFilteredAdmin } = useAuth();
    const { trackEvent } = useTrackEvent();

    // Multi-Entity Upload Feature (Premium)
    const { hasFeature: hasMultiEntityUpload, loading: featureLoading } = useFeatureEntitlement(
        userData?.companyId,
        'multi_entity_upload'
    );
    const [showMultiEntity, setShowMultiEntity] = useState(false);

    // Get entity mappings for multi-entity uploader
    const expectedEntities = useMemo(() => {
        // These would come from entity_mappings collection in a real implementation
        // For now, return empty - will be populated when entity mappings exist
        return [] as string[];
    }, []);

    const entityNames = useMemo(() => {
        // Map entity codes to office names
        const names: Record<string, string> = {};
        offices.forEach((office: { id: string; name: string; entityCode?: string }) => {
            if (office.entityCode) {
                names[office.entityCode] = office.name;
            }
        });
        return names;
    }, [offices]);

    // Parse filename to extract run number, month, and year
    const parseFilenameForMetadata = (filename: string): {
        runNumber: 1 | 2 | 3 | null;
        month: string | null;
        year: number | null;
    } => {
        // Extract run number (RUN 1, RUN_2, RUN-3, etc.)
        const runMatch = filename.match(/RUN[_\s-]*(\d)/i);
        const runNumber = runMatch ? parseInt(runMatch[1]) as 1 | 2 | 3 : null;

        // Month abbreviation to full name mapping
        const monthMap: { [key: string]: string } = {
            'jan': 'January', 'january': 'January',
            'feb': 'February', 'february': 'February',
            'mar': 'March', 'march': 'March',
            'apr': 'April', 'april': 'April',
            'may': 'May',
            'jun': 'June', 'june': 'June',
            'jul': 'July', 'july': 'July',
            'aug': 'August', 'august': 'August',
            'sep': 'September', 'sept': 'September', 'september': 'September',
            'oct': 'October', 'october': 'October',
            'nov': 'November', 'november': 'November',
            'dec': 'December', 'december': 'December'
        };

        // Extract month (full names or abbreviations)
        const monthMatch = filename.match(/(Jan|January|Feb|February|Mar|March|Apr|April|May|Jun|June|Jul|July|Aug|August|Sep|Sept|September|Oct|October|Nov|November|Dec|December)/i);
        const month = monthMatch ? monthMap[monthMatch[1].toLowerCase()] : null;

        // Extract year (20XX format or 2-digit format like " 25" or "_25" for 2025)
        let year: number | null = null;
        const fullYearMatch = filename.match(/20(\d{2})/);
        if (fullYearMatch) {
            year = parseInt(`20${fullYearMatch[1]}`);
        } else {
            // Try to match 2-digit year patterns:
            // - "_25" (underscore + digits)
            // - " 25" (space + digits) - common in Entover files
            // - " 25." or " 25 " (space + digits + delimiter)
            const shortYearMatch = filename.match(/[\s_](\d{2})(?:[\s\.\(\)]|$)/);
            if (shortYearMatch) {
                const shortYear = parseInt(shortYearMatch[1]);
                // Assume 20XX century for years 00-99
                year = 2000 + shortYear;
            }
        }

        return { runNumber, month, year };
    };

    /**
     * Auto-detect template from filename patterns
     *
     * Known patterns:
     * - AFG: COMM_STMT_*-MASTER-*.csv (e.g., COMM_STMT_ENTOURAGE CRANBOURNE PTY LTD-MASTER-1051153.csv)
     * - Loan Market: *_AGMT_RUN_*_*.xlsx with UNDERSCORES (e.g., V316295PL_AGMT_RUN_3_Sep_25.xlsx)
     * - Entover: * AGMT RUN * *.xlsx with SPACES (e.g., V02970PL AGMT RUN 1 Jul 25.xlsx)
     * - Entors: *.XLS with "Payee" in name (e.g., Entors Pty Ltd (Payee)(5148).XLS)
     *
     * Returns the detected template or null if no pattern matches
     */
    const detectTemplateFromFilename = (filename: string, availableTemplates: Template[]): Template | null => {
        const lowerFilename = filename.toLowerCase();

        // AFG pattern: COMM_STMT_*-MASTER-*.csv
        if (/comm_stmt_.*-master-.*\.csv$/i.test(filename)) {
            const afgTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('australian finance group') ||
                t.templateName?.toLowerCase().includes('afg')
            );
            if (afgTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected AFG template from filename pattern: ${filename}`);
                return afgTemplate;
            }
        }

        // NOTE: Entover and Entical BOTH use the pattern "V######PL AGMT RUN # Mon YY.xlsx"
        // (spaces, not underscores). These templates are INDISTINGUISHABLE by filename alone.
        // The only way to tell them apart is by file content (Introducer column).
        // Therefore, we DO NOT auto-detect for this pattern - user must manually select.
        // This prevents data corruption from uploading Entical files with Entover template.
        // See: docs/handovers/2025-12-11-ENTICAL-ENTOVER-DETECTION.md

        // Loan Market pattern: *_AGMT_RUN_*_*.xlsx with UNDERSCORES
        // e.g., "V316295PL_AGMT_RUN_3_Sep_25.xlsx"
        if (/_agmt_run_\d+_/i.test(filename) && /\.(xlsx|xls)$/i.test(filename)) {
            const lmTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('loan market')
            );
            if (lmTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected Loan Market template from filename pattern (underscores): ${filename}`);
                return lmTemplate;
            }
        }

        // Loan Market pattern 2: * LW RUN # * (Timms entity files with spaces)
        // e.g., "BRIS3.02 Andrew Timms (TRL PUR) LW RUN 3 Jul 23.xlsx"
        if (/\bLW\s+RUN\s+\d+\b/i.test(filename) && /\.(xlsx|xls)$/i.test(filename)) {
            const lmTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('loan market')
            );
            if (lmTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected Loan Market template from filename pattern (LW spaces): ${filename}`);
                return lmTemplate;
            }
        }

        // Entors pattern: *.XLS files with "Payee" in name (multi-sheet format)
        if (lowerFilename.endsWith('.xls') && lowerFilename.includes('payee')) {
            const entorsTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('entors') ||
                t.isMultiSheet === true
            );
            if (entorsTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected Entors template from filename pattern: ${filename}`);
                return entorsTemplate;
            }
        }

        // RCTI PDF pattern (for paired uploads) - detect AFG from RCTI filename
        if (/rcti-.*-\d{8}-\d+\.pdf$/i.test(filename)) {
            // RCTIs are typically AFG format
            const afgTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('australian finance group') ||
                t.templateName?.toLowerCase().includes('afg')
            );
            if (afgTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected AFG template from RCTI filename: ${filename}`);
                return afgTemplate;
            }
        }

        // WLTH Lend pattern: *WLTH*.xlsx (lender direct, multi-sheet)
        if (/wlth.*\.xlsx$/i.test(filename)) {
            const wlthTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('wlth')
            );
            if (wlthTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected WLTH template from filename pattern: ${filename}`);
                return wlthTemplate;
            }
        }

        // Vision (AMAG) pattern: AMAG *.xlsx/.xls (acquired-book trail aggregator, multi-sheet).
        // AMAG filenames ("AMAG May 2026.xlsx", "AMAG pymt 1 Nov 2024.xlsx") carry NO digit-bearing
        // entity anchor, so without this branch selectedTemplate stays a template id and the upload
        // loses entityCode → entity_code NULL on the client path (the server now has a loan_books
        // fallback, but this keeps the dropdown honest and auto-selects Vision). Mirrors /^AMAG\b/
        // in aggregatorRegistry ('VISION').
        if (/^AMAG\b/i.test(filename) && /\.(xlsx|xls)$/i.test(filename)) {
            const visionTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('vision') ||
                t.templateName?.toLowerCase().includes('amag') ||
                (t as any).aggregator === 'vision'
            );
            if (visionTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected Vision (AMAG) template from filename pattern: ${filename}`);
                return visionTemplate;
            }
        }

        // ORDE Financial pattern: BrokerTransaction*.xls (paired with RCTI PDF)
        // Also match the RCTI PDF: BrokerTransaction RCTI ORD-*.pdf
        if (/brokertransaction.*\.(xls|pdf)$/i.test(filename)) {
            const ordeTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('orde')
            );
            if (ordeTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected ORDE Financial template from filename pattern: ${filename}`);
                return ordeTemplate;
            }
        }

        // Astute Financial pattern: TaxInvoices_*.xlsx
        if (/TaxInvoices_.*\.xlsx$/i.test(filename)) {
            const astuteTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('astute')
            );
            if (astuteTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected Astute Financial template from filename pattern: ${filename}`);
                return astuteTemplate;
            }
        }

        // Connective pattern: Commission Export*.xls
        if (/Commission Export.*\.xls$/i.test(filename)) {
            const connectiveTemplate = availableTemplates.find(t =>
                t.templateName?.toLowerCase().includes('connective')
            );
            if (connectiveTemplate) {
                console.log(`[TEMPLATE_DETECT] Detected Connective template from filename pattern: ${filename}`);
                return connectiveTemplate;
            }
        }

        console.log(`[TEMPLATE_DETECT] No template pattern matched for: ${filename}`);
        return null;
    };

    const [templates, setTemplates] = useState<Template[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');

    // Resolve loan book selection → query params (templateId + optional entityCode)
    // UploadPage: Currently no data-query hooks use entityCode, but resolution is
    // available for future use (e.g., if upload history queries are added).
    const resolvedParams = useMemo(() =>
        resolveLoanBookSelection(selectedTemplate, loanBooks),
        [selectedTemplate, loanBooks]
    );

    const [selectedOffice, setSelectedOffice] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [pdfFile, setPdfFile] = useState<File | null>(null);  // Paired RCTI PDF for pdf_period_range ("Choice Runs")
    const [uploading, setUploading] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [error, setError] = useState<string>('');
    // Phase F (fail-loud banner): track the in-flight upload by its uploadId and derive the
    // banner from the live client_processing_queue docs — green ONLY on completed+records,
    // red on failed, neutral until terminal, timeout fallback for the limbo case.
    const [activeUploadId, setActiveUploadId] = useState<string | null>(null);
    const [lastUploadName, setLastUploadName] = useState<string>('');
    const [processingTimedOut, setProcessingTimedOut] = useState<boolean>(false);

    // Run period selector state
    const [selectedRun, setSelectedRun] = useState<1 | 2 | 3>(1);
    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

    // Invoice date for AFG template (which doesn't have invoice date in CSV)
    const [invoiceDate, setInvoiceDate] = useState<string>('');
    
    const [uploadedFiles, setUploadedFiles] = useState<SourceFile[]>([]);
    const [loadingFiles, setLoadingFiles] = useState<boolean>(true);
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const [deleting, setDeleting] = useState<boolean>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [expandedFileId, setExpandedFileId] = useState<string | null>(null);
    const [fileToDelete, setFileToDelete] = useState<SourceFile | null>(null);
    const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState<boolean>(false);
    const [batchDeleting, setBatchDeleting] = useState<boolean>(false);
    const [showResultModal, setShowResultModal] = useState<boolean>(false);
    // Track files being deleted to prevent Firestore listener from reinstating them
    const pendingDeleteIdsRef = useRef<Set<string>>(new Set());
    const [resultModalMessage, setResultModalMessage] = useState<string>('');
    const [resultModalType, setResultModalType] = useState<'success' | 'error'>('success');

    // Mismatch warning dialog state
    const [showMismatchWarning, setShowMismatchWarning] = useState<boolean>(false);
    const [mismatchDetails, setMismatchDetails] = useState<{
        originalInfo: string;
        selectedInfo: string;
        mismatches: string[];
    } | null>(null);
    const [mismatchResolve, setMismatchResolve] = useState<((value: boolean) => void) | null>(null);

    // Template mismatch warning dialog state
    const [showTemplateMismatch, setShowTemplateMismatch] = useState<boolean>(false);
    const [templateMismatchDetails, setTemplateMismatchDetails] = useState<{
        detectedTemplate: string;
        selectedTemplate: string;
        filename: string;
    } | null>(null);
    const [templateMismatchResolve, setTemplateMismatchResolve] = useState<((value: 'switch' | 'proceed') => void) | null>(null);

    const [activeOfficeTab, setActiveOfficeTab] = useState<string | null>(null);
    const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());
    const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
    const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
    
    const uploadedFilesSectionRef = useRef<HTMLDivElement | null>(null);
    const isInitialMount = useRef<boolean>(true);

    const organizedFiles = useMemo(() =>
        organizeFilesByOfficeTemplateYearMonth(uploadedFiles, templates, offices, loanBooks),
        [uploadedFiles, templates, offices, loanBooks]
    );

    const handleTabClick = (officeName: string) => {
        setActiveOfficeTab(officeName);
    };

    const sortedOfficeNames = useMemo(() => {
        if (!organizedFiles) return [];
        const officeNames = Object.keys(organizedFiles);
        if (!activeOfficeTab) return officeNames;

        return officeNames.sort((a: any, b: any) => {
            if (a === activeOfficeTab) return -1;
            if (b === activeOfficeTab) return 1;
            return a.localeCompare(b);
        });
    }, [organizedFiles, activeOfficeTab]);

    // Mismatch warning dialog handlers
    const handleMismatchCancel = () => {
        setShowMismatchWarning(false);
        setMismatchDetails(null);
        if (mismatchResolve) {
            mismatchResolve(false);
            setMismatchResolve(null);
        }
    };

    const handleMismatchOK = () => {
        setShowMismatchWarning(false);
        setMismatchDetails(null);
        if (mismatchResolve) {
            mismatchResolve(true);
            setMismatchResolve(null);
        }
    };

    // Template mismatch dialog handlers
    const handleTemplateMismatchSwitch = () => {
        setShowTemplateMismatch(false);
        setTemplateMismatchDetails(null);
        if (templateMismatchResolve) {
            templateMismatchResolve('switch');
            setTemplateMismatchResolve(null);
        }
    };

    const handleTemplateMismatchProceed = () => {
        setShowTemplateMismatch(false);
        setTemplateMismatchDetails(null);
        if (templateMismatchResolve) {
            templateMismatchResolve('proceed');
            setTemplateMismatchResolve(null);
        }
    };

    useEffect(() => {
        const officeKeys = Object.keys(organizedFiles);
        if (officeKeys.length > 0 && !activeOfficeTab) {
            setActiveOfficeTab(officeKeys[0]);
        }
    }, [organizedFiles, activeOfficeTab]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            uploadedFilesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [activeOfficeTab]);

    useEffect(() => {
        if (offices && offices.length > 0 && !selectedOffice) {
            setSelectedOffice(offices[0].id);
        }
    }, [offices, selectedOffice]);

    // Auto-set default month to current month
    useEffect(() => {
        const currentMonth = new Date().toLocaleString('default', { month: 'long' });
        setSelectedMonth(currentMonth);
    }, []);
    
    // Office IDs for filtering uploaded files (office_admin sees only their offices)
    const userOfficeIds = useMemo(() => {
        if (!offices || offices.length === 0) return null;
        return new Set(offices.map((o: any) => o.id));
    }, [offices]);

    const isRestrictedRole = userData?.role === 'office_admin' || userData?.role === 'broker' || userData?.role === 'broker_assistant';

    useEffect(() => {
        if (!userData?.companyId) return;
        const sourceDocsQuery = query(
            collection(db, 'client_processing_queue'),
            where('companyId', '==', userData.companyId),
            where('status', 'in', ['pending', 'processing', 'completed', 'failed']),
            orderBy('uploadTimestamp', 'desc')
        );
        const unsubscribe = onSnapshot(
          sourceDocsQuery,
          (snapshot: any) => {
            const filesData = snapshot.docs
              .map((doc: any) => ({ id: doc.id, ...doc.data() } as SourceFile))
              .filter((f: any) => f.status !== 'deleted')
              // Filter out files that are pending deletion to prevent race condition reinstating
              .filter((f: any) => !pendingDeleteIdsRef.current.has(f.id))
              // Office scoping: office_admin/broker only see files from their offices
              .filter((f: SourceFile) => {
                if (!isRestrictedRole || !userOfficeIds) return true;
                return !f.officeId || userOfficeIds.has(f.officeId);
              });
            setUploadedFiles(filesData);
            setLoadingFiles(false);
          },
          (err: Error) => {
  // console.error('source_documents snapshot error', err);
            setError(
              err?.message?.includes('index')
                ? 'Unable to load uploaded files. Please contact support if this persists.'
                : 'Failed to load the Uploaded Files list.'
            );
            setLoadingFiles(false);
          }
        );
        return () => unsubscribe();
    }, [userData, userOfficeIds, isRestrictedRole]);

    // Phase F (fail-loud banner): derive the post-upload banner from the live queue docs
    // (uploadedFiles) — green ONLY on completed + records, red on failed, neutral until then.
    const processingBanner = useMemo(
        () => deriveUploadProcessingBanner(activeUploadId, uploadedFiles as any[], processingTimedOut, lastUploadName),
        [activeUploadId, uploadedFiles, processingTimedOut, lastUploadName]
    );

    // Limbo fallback: if Stage 1 throws before the queue doc is ever created (or processing
    // stalls), flip to a neutral "still processing — check the Files list" after ~4 minutes.
    useEffect(() => {
        if (!activeUploadId) return;
        const timer = setTimeout(() => setProcessingTimedOut(true), 4 * 60 * 1000);
        return () => clearTimeout(timer);
    }, [activeUploadId]);

    // Templates from centralized context (no per-page Firestore query)
    useEffect(() => {
        const templatesList = (userVisibleTemplates || []).map((t: any) => ({
            id: t.id,
            ...t
        } as Template));
        // Keep this UNCONDITIONAL — gating it on loanBooksLoading would blank legacy
        // (loan-book-less) dropdowns while loan books load.
        setTemplates(templatesList);
        // Don't seed until the option namespace is final. useLoanBooks starts
        // loading:false → flips true during fetch; seeding now could land a template id
        // that matches no loan-book option for a unified tenant (the "shows X, sends Y"
        // bug). The submit guard below is the backstop for any first-mount transient.
        if (loanBooksLoading) return;
        // Seed/validate selectedTemplate against the SAME option source the <select>
        // renders, so the displayed option always equals the submitted value. Pure,
        // RBAC-aware, unit-tested in utils/__tests__/loanBookResolution.test.ts.
        const seed = resolveUploadSeedSelection(loanBooks, templatesList, selectedTemplate);
        if (seed !== null) {
            setSelectedTemplate(seed);
        }
    }, [userVisibleTemplates, loanBooks, loanBooksLoading, selectedTemplate]);

    // Auto-select Run 1 for single-run templates (e.g., Entors)
    const selectedTemplateData = useMemo(() => {
        const resolvedId = resolvedParams.templateId || selectedTemplate;
        return templates.find(t => t.id === resolvedId);
    }, [templates, resolvedParams, selectedTemplate]);
    const isSingleRunTemplate = selectedTemplateData?.runHandling?.strategy === 'single';
    // "Choice Runs" — auto-derives run + month from a paired RCTI PDF the user must also upload.
    const isPdfPeriodRange = selectedTemplateData?.dateExtraction?.strategy === 'pdf_period_range';
    // Jul 2026 discharge capture: templates with pdfReports capability also carry
    // the RCTI PDF (Stage 2 parses its Inactive Loan Report page). The existing
    // pdf_period_range trigger is untouched; the capability trigger is additive.
    const offersPairedPdf = isPdfPeriodRange || Boolean(selectedTemplateData?.pdfReports?.inactiveLoanReport);
    // PDF statement templates (NMB): the upload file IS the statement PDF.
    const isPdfStatementTemplate = (selectedTemplateData?.pdfStatement?.parsers?.length ?? 0) > 0;
    const pairedPdfMandatory = isPdfPeriodRange || selectedTemplateData?.pdfReports?.inactiveLoanReport?.required === true;

    useEffect(() => {
        if (isSingleRunTemplate) {
            setSelectedRun(1);
        }
    }, [isSingleRunTemplate]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);

            // Parse filename and auto-populate UI
            const parsed = parseFilenameForMetadata(selectedFile.name);

            const detectedParts: string[] = [];

            // AUTO-DETECT TEMPLATE FROM FILENAME PATTERN
            // This prevents uploading AFG files to Loan Market template (and vice versa)
            const detectedTemplate = detectTemplateFromFilename(selectedFile.name, templates);
            if (detectedTemplate && detectedTemplate.id !== (resolvedParams.templateId || selectedTemplate)) {
                // For unified mode, find the loan book that maps to detected template
                const matchingLoanBook = loanBooks.find(lb => lb.templateId === detectedTemplate.id);
                setSelectedTemplate(matchingLoanBook ? matchingLoanBook.id : detectedTemplate.id);
                detectedParts.push(`Template: ${detectedTemplate.templateName}`);
                console.log(`[UPLOAD] Auto-selected template: ${detectedTemplate.templateName} (${detectedTemplate.id})`);
            }

            if (parsed.runNumber) {
                setSelectedRun(parsed.runNumber);
                detectedParts.push(`RUN ${parsed.runNumber}`);
            }

            if (parsed.month) {
                setSelectedMonth(parsed.month);
                detectedParts.push(parsed.month);
            }

            if (parsed.year) {
                setSelectedYear(parsed.year);
                detectedParts.push(parsed.year.toString());
            }

            // Show what was detected
            if (detectedParts.length >= 3) {
                setMessage(`✓ Auto-detected from filename: ${detectedParts.join(', ')}`);
                setError('');
            } else if (detectedParts.length > 0) {
                setMessage(`⚠️ Partially detected: ${detectedParts.join(', ')}. Please verify and complete the remaining fields.`);
                setError('');
            } else {
                setError('⚠️ Could not recognise run/month/year from filename. Please select manually.');
                setMessage('');
            }
        }
    };
    
    const handleUpload = async () => {
    const currentFile = file;
    const currentUser = auth.currentUser;

    if (!currentFile || !userData?.companyId || !selectedTemplate || !selectedOffice || !currentUser) {
        const missing = [];
        if (!currentFile) missing.push('a file');
        if (!selectedTemplate) missing.push('a template');
        if (!selectedOffice) missing.push('an office');
        setError(`Please select ${missing.join(' and ')} to proceed.`);
        return;
    }

    if (!selectedMonth) {
        setError('Please select a month for this commission run');
        return;
    }

    // Mandatory paired RCTI PDF: pdf_period_range derives run + month from it;
    // pdfReports.required templates lose this window's discharges permanently
    // without it (the Inactive Loan Report is period-scoped).
    if (pairedPdfMandatory && !pdfFile) {
        setError('This loan book needs the matching RCTI PDF. Please attach both the data file and the PDF.');
        return;
    }

    // Check if AFG template requires invoice date
    const isAFG = selectedTemplateData?.templateName?.includes('Australian Finance Group');
    if (isAFG && !invoiceDate) {
        setError('Please select an invoice date for AFG commission files');
        return;
    }

    // TEMPLATE-FILE MISMATCH VALIDATION
    // Warn if file pattern doesn't match selected template (prevents processing failures)
    const detectedTemplate = detectTemplateFromFilename(currentFile.name, templates);
    if (detectedTemplate && detectedTemplate.id !== (resolvedParams.templateId || selectedTemplate)) {
        // Show VALUTTEN-styled modal and wait for user response
        const userChoice = await new Promise<'switch' | 'proceed'>((resolve) => {
            setTemplateMismatchDetails({
                detectedTemplate: detectedTemplate.templateName || 'Unknown',
                selectedTemplate: selectedTemplateData?.templateName || 'Unknown',
                filename: currentFile.name
            });
            setTemplateMismatchResolve(() => resolve);
            setShowTemplateMismatch(true);
        });

        if (userChoice === 'switch') {
            // Mirror the file-attach auto-detect (above): in unified mode the <select>
            // options are loan-book ids, so map the detected template back to its loan
            // book — otherwise we'd seed a template id that matches no option and the
            // displayed selection would diverge from submitted state again.
            const matchingLoanBook = loanBooks.find(lb => lb.templateId === detectedTemplate.id);
            setSelectedTemplate(matchingLoanBook ? matchingLoanBook.id : detectedTemplate.id);
            setMessage(`✓ Switched to ${detectedTemplate.templateName}. Please click Upload again.`);
            return; // Let user review and click upload again
        }
        // User chose to proceed with mismatched template - at their own risk
        console.warn(`[UPLOAD] User proceeding with mismatched template. File: ${currentFile.name}, Selected: ${selectedTemplateData?.templateName}, Detected: ${detectedTemplate.templateName}`);
    }

    // CRITICAL: Validate UI selection against original filename
    const parsed = parseFilenameForMetadata(currentFile.name);

    const mismatches: string[] = [];
    if (parsed.runNumber && parsed.runNumber !== selectedRun) {
        mismatches.push(`Run (file: ${parsed.runNumber}, selected: ${selectedRun})`);
    }
    if (parsed.month && parsed.month !== selectedMonth) {
        mismatches.push(`Month (file: ${parsed.month}, selected: ${selectedMonth})`);
    }
    if (parsed.year && parsed.year !== selectedYear) {
        mismatches.push(`Year (file: ${parsed.year}, selected: ${selectedYear})`);
    }

    if (mismatches.length > 0) {
        const originalInfo = [
            parsed.runNumber ? `RUN ${parsed.runNumber}` : null,
            parsed.month,
            parsed.year
        ].filter(Boolean).join(', ');

        const selectedInfo = `RUN ${selectedRun}, ${selectedMonth}, ${selectedYear}`;

        // Show custom dialog and wait for user response
        const proceed = await new Promise<boolean>((resolve) => {
            setMismatchDetails({ originalInfo, selectedInfo, mismatches });
            setMismatchResolve(() => resolve);
            setShowMismatchWarning(true);
        });

        if (!proceed) {
            setUploading(false);
            return; // Block upload
        }
    }

    setError('');
    setMessage('');
    // Clear any prior upload's result banner before starting a new one.
    setActiveUploadId(null);
    setProcessingTimedOut(false);
    setUploading(true);

    try {
        // Use PARSED values if available (filename priority), fallback to UI
        const finalRun = parsed.runNumber || selectedRun;
        const finalMonth = parsed.month || selectedMonth;
        const finalYear = parsed.year || selectedYear;

        // AUTO-GENERATE COMPLIANT FILENAME from parsed/validated values
        // Format: "RUN X - Month YYYY.ext"
        const extension = currentFile.name.split('.').pop() || 'xlsx';
        const compliantFilename = formatUploadDisplayName(currentFile.name, {
            runNumber: finalRun,
            month: finalMonth,
            year: finalYear,
            extension
        });

        console.log(`[UPLOAD] Original filename: ${currentFile.name}`);
        console.log(`[UPLOAD] Parsed from filename:`, parsed);
        console.log(`[UPLOAD] Final compliant filename: ${compliantFilename}`);
        console.log(`[UPLOAD] Final run number: ${finalRun}`);

        // FAIL-CLOSED: for unified tenants (loan books present) the selection MUST
        // resolve to a templateId. If it doesn't, the <select> value is a stale/invalid
        // id (e.g. the first-mount transient template-id seed before loan books loaded)
        // — block rather than fall through to a raw `selectedTemplate` that could route
        // the file to the wrong parser. The `|| selectedTemplate` fallback below is kept
        // ONLY for the legacy loanBooks.length === 0 path.
        if (loanBooks.length > 0 && !resolvedParams.templateId) {
            setError('Loan book selection could not be resolved. Please re-select the loan book and try again.');
            setUploading(false);
            return;
        }

        // STEP 1: Request Signed URL
        const getSignedUrl = httpsCallable(functions, 'issueSignedUploadUrl');

        setMessage('Authorizing upload...');

        const result = await getSignedUrl({
            companyId: userData.companyId,
            officeId: selectedOffice,
            templateId: resolvedParams.templateId || selectedTemplate,
            entityCode: resolvedParams.entityCode || undefined,  // From loan_book resolution — fallback for non-entity-prefixed filenames
            fileName: compliantFilename,  // Use compliant filename
            originalFileName: currentFile.name,  // Original filename for entity code extraction
            fileSize: currentFile.size,
            contentType: currentFile.type || 'application/octet-stream',
            invoiceDate: invoiceDate || undefined,  // For AFG template which lacks invoice date in CSV
            // Send the paired RCTI PDF (run/month derivation and/or Stage 2 discharge capture).
            ...(offersPairedPdf && pdfFile ? {
                pairedPdf: {
                    fileName: pdfFile.name,
                    fileSize: pdfFile.size,
                    contentType: 'application/pdf',
                },
            } : {}),
        });

        const { signedUrl, uploadId, pairedPdfSignedUrl } = result.data as {
            signedUrl: string;
            uploadId: string;
            storagePath: string;
            pairedPdfSignedUrl?: string;
        };

        // STEP 2: Upload to Signed URL(s)
        setMessage('Uploading file...');

        // Upload the RCTI PDF FIRST so it is in storage when the XLS
        // finalize triggers processing (single-trigger; backend reads it by path).
        if (offersPairedPdf && pdfFile && pairedPdfSignedUrl) {
            const pdfResponse = await fetch(pairedPdfSignedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/pdf' },
                body: pdfFile,
            });
            if (!pdfResponse.ok) {
                throw new Error(`RCTI PDF upload failed: ${pdfResponse.status}`);
            }
        }

        const uploadResponse = await fetch(signedUrl, {
            method: 'PUT',
            body: currentFile,
        });

        if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.status}`);
        }

        trackEvent('commission_uploaded', {
          templateId: selectedTemplate,
          runNumber: finalRun,
          month: finalMonth,
          year: finalYear,
        });

        // Phase F: do NOT go green here — the PUT only means bytes landed, not that
        // processing succeeded. Track this upload by uploadId; the banner is derived
        // from the live queue doc and goes green only on completed + records added.
        setMessage('');
        setLastUploadName(compliantFilename);
        setProcessingTimedOut(false);
        setActiveUploadId(uploadId);
        setFile(null);
        setPdfFile(null);
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        const pdfInput = document.getElementById('rcti-pdf-upload') as HTMLInputElement;
        if (pdfInput) pdfInput.value = '';

    } catch (err: any) {
        trackEvent('upload_failed', { error: String(err.message), templateId: selectedTemplate });
        setError(`Upload failed: ${err.message}`);
    } finally {
        setUploading(false);
    }
};
    
    const handleDeleteConfirm = async () => {
        if (!fileToDelete || !userData?.companyId) return;
        setDeleting(true);
        setError('');

        // Gmail-style: Remove from UI immediately (optimistic update)
        const deletedFile = fileToDelete;
        const previousFiles = uploadedFiles;

        // Track pending delete to prevent Firestore listener from reinstating
        pendingDeleteIdsRef.current.add(deletedFile.id);
        setUploadedFiles(prev => prev.filter(f => f.id !== deletedFile.id));

        // Close modal immediately for snappy UX
        setShowDeleteConfirm(false);
        setFileToDelete(null);
        setDeleting(false);

        try {
            const callable = httpsCallable(functions, 'deleteSourceDocument');
            const deleteResult = await callable({
                companyId: userData.companyId,
                sourceDocId: deletedFile.id,
                storagePath: deletedFile.storagePath
            });
            // Remove from pending set on success
            pendingDeleteIdsRef.current.delete(deletedFile.id);

            // The file IS deleted either way. But when the backend could not
            // re-derive the aggregates, the affected months under-report until
            // someone rebuilds them — say so rather than showing a bare success.
            const { aggregatesStale, affectedMonths } = (deleteResult.data ?? {}) as {
                aggregatesStale?: boolean;
                affectedMonths?: string[];
            };
            if (aggregatesStale) {
                const months = affectedMonths?.length ? ` (${affectedMonths.join(', ')})` : '';
                setResultModalMessage(
                    `Deleted "${deletedFile.fileName}", but the monthly totals for the affected period${months} could not be recalculated. Those months will read low until they are rebuilt. Please report this.`
                );
                setResultModalType('error');
            } else {
                setResultModalMessage(`Deleted "${deletedFile.fileName}".`);
                setResultModalType('success');
            }
            setShowResultModal(true);
        } catch (err: any) {
            // Remove from pending set on failure
            pendingDeleteIdsRef.current.delete(deletedFile.id);
            // Rollback: Restore file to list if delete failed
            setUploadedFiles(previousFiles);
            // Show centered error modal
            setResultModalMessage(`Failed to delete file: ${err.message}`);
            setResultModalType('error');
            setShowResultModal(true);
        }
    };

    const handleBatchDeleteConfirm = async () => {
        if (selectedFiles.size === 0 || !userData?.companyId) return;

        const filesToDelete = uploadedFiles.filter(f => selectedFiles.has(f.id));
        const previousFiles = uploadedFiles;
        const idsToDelete = new Set(filesToDelete.map(f => f.id));

        // Track pending deletes to prevent Firestore listener from reinstating them
        filesToDelete.forEach(f => pendingDeleteIdsRef.current.add(f.id));

        // Gmail-style: Remove all selected files immediately
        setUploadedFiles(prev => prev.filter(f => !idsToDelete.has(f.id)));
        setSelectedFiles(new Set());
        setShowBatchDeleteConfirm(false);
        setBatchDeleting(false);

        const deleteCallable = httpsCallable(functions, 'deleteSourceDocument');

        let successCount = 0;
        let failCount = 0;
        const errors: string[] = [];
        const failedFileIds = new Set<string>();
        // Months whose aggregates the backend could not re-derive. The deletes
        // succeeded, so these are NOT failures, but the operator still has to
        // know those months now read low.
        const staleMonths = new Set<string>();

        // Delete files sequentially to avoid overwhelming the backend
        for (const file of filesToDelete) {
            try {
                const deleteResult = await deleteCallable({
                    companyId: userData.companyId,
                    sourceDocId: file.id,
                    storagePath: file.storagePath
                });
                successCount++;
                const { aggregatesStale, affectedMonths } = (deleteResult.data ?? {}) as {
                    aggregatesStale?: boolean;
                    affectedMonths?: string[];
                };
                if (aggregatesStale) {
                    (affectedMonths ?? []).forEach(m => staleMonths.add(m));
                }
                // Remove from pending set once successfully deleted
                pendingDeleteIdsRef.current.delete(file.id);
            } catch (err: any) {
                failCount++;
                failedFileIds.add(file.id);
                errors.push(`${file.fileName}: ${err.message}`);
                // Remove from pending set on failure (will be restored below)
                pendingDeleteIdsRef.current.delete(file.id);
            }
        }

        // Rollback: Restore only the files that failed to delete
        if (failCount > 0) {
            const filesToRestore = previousFiles.filter(f => failedFileIds.has(f.id));
            setUploadedFiles(prev => [...prev, ...filesToRestore].sort((a, b) => {
                const dateA = a.uploadedAt?.toMillis() || 0;
                const dateB = b.uploadedAt?.toMillis() || 0;
                return dateB - dateA;
            }));
        }

        // A stale-aggregate warning outranks a clean success: the files went, but
        // the totals for those months are wrong until someone rebuilds them.
        const staleSuffix = staleMonths.size > 0
            ? ` WARNING: the monthly totals for ${Array.from(staleMonths).sort().join(', ')} could not be recalculated and will read low until rebuilt. Please report this.`
            : '';

        // Show centered result modal
        if (failCount === 0) {
            setResultModalMessage(`Successfully deleted ${successCount} file(s).${staleSuffix}`);
            setResultModalType(staleMonths.size > 0 ? 'error' : 'success');
            setShowResultModal(true);
        } else if (successCount === 0) {
            setResultModalMessage(`Failed to delete all ${failCount} file(s). ${errors.join('; ')}`);
            setResultModalType('error');
            setShowResultModal(true);
        } else {
            setResultModalMessage(`Deleted ${successCount} file(s). Failed: ${failCount}. Errors: ${errors.join('; ')}${staleSuffix}`);
            setResultModalType('success');
            setShowResultModal(true);
        }
    };

    const handleFileSelect = (fileId: string) => {
        setSelectedFiles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                newSet.delete(fileId);
            } else {
                newSet.add(fileId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (filesToSelect: SourceFile[]) => {
        setSelectedFiles(prev => {
            const newSet = new Set(prev);
            const allSelected = filesToSelect.every(file => newSet.has(file.id));
            filesToSelect.forEach(file => {
                if (allSelected) {
                    newSet.delete(file.id);
                } else {
                    newSet.add(file.id);
                }
            });
            return newSet;
        });
    };
    
    const formatDate = (timestamp?: Timestamp): string => {
        return timestamp?.toDate().toLocaleString() || 'Unknown';
    };
    
    // Determine effective status based on status, recordCount, and isDuplicate
    const getEffectiveStatus = (file: SourceFile): string => {
        if (file.status === 'completed') {
            // File-level duplicate detected by hash matching
            if (file.isDuplicate) {
                return 'duplicate';
            }
            // Completed with records = normal success
            if (file.recordCount && file.recordCount > 0) {
                return 'completed';
            }
            // Completed with 0 records, no duplicate flag — empty/no new records
            return 'empty';
        }
        return file.status;
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return 'bg-green-900 text-green-300 px-2 py-1 rounded-full text-xs';
            case 'duplicate':
                return 'bg-blue-900 text-blue-300 px-2 py-1 rounded-full text-xs';
            case 'empty':
                return 'bg-gray-700 text-gray-300 px-2 py-1 rounded-full text-xs';
            case 'processing':
                return 'bg-yellow-900 text-yellow-300 px-2 py-1 rounded-full text-xs animate-pulse';
            case 'failed':
                return 'bg-red-900 text-red-300 px-2 py-1 rounded-full text-xs';
            default:
                return 'bg-gray-700 text-gray-300 px-2 py-1 rounded-full text-xs';
        }
    };

    const toggleTemplate = (templateKey: string) => {
        setExpandedTemplates(prev => {
            const newSet = new Set(prev);
            if (newSet.has(templateKey)) {
                newSet.delete(templateKey);
            } else {
                newSet.add(templateKey);
            }
            return newSet;
        });
    };

    const toggleYear = (yearKey: string, months: object) => {
        const isCurrentlyExpanded = expandedYears.has(yearKey);
        setExpandedYears(prev => {
            const newSet = new Set(prev);
            if (isCurrentlyExpanded) {
                newSet.delete(yearKey);
            } else {
                newSet.add(yearKey);
            }
            return newSet;
        });

        if (months) {
            const monthKeys = Object.keys(months).map((monthKey: any) => `${yearKey}_${monthKey}`);
            setExpandedMonths(prev => {
                const newSet = new Set(prev);
                if (isCurrentlyExpanded) {
                    monthKeys.forEach(k => newSet.delete(k));
                } else {
                    monthKeys.forEach(k => newSet.add(k));
                }
                return newSet;
            });
        }
    };

    const toggleMonth = (monthKey: string) => {
        setExpandedMonths(prev => {
            const newSet = new Set(prev);
            if (newSet.has(monthKey)) {
                newSet.delete(monthKey);
            } else {
                newSet.add(monthKey);
            }
            return newSet;
        });
    };

    return (
        <div className="space-y-6">
            <PageHeader
                icon={Upload}
                title="Upload Commission"
                subtitle="Upload RCTI and commission CSV files for processing"
            />

            <div>
            {/* Multi-Entity Upload Section (Premium Feature) */}
            {!featureLoading && (
                <div className="max-w-4xl mx-auto mb-10">
                    {hasMultiEntityUpload ? (
                        <>
                            {/* Toggle between single and multi-entity upload */}
                            <div className="flex items-center justify-center gap-4 mb-6">
                                <button
                                    onClick={() => setShowMultiEntity(false)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        !showMultiEntity
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    }`}
                                >
                                    Single File Upload
                                </button>
                                <button
                                    onClick={() => setShowMultiEntity(true)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        showMultiEntity
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    }`}
                                >
                                    Multi-Entity Upload
                                    <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500 text-black rounded-full">
                                        Premium
                                    </span>
                                </button>
                            </div>

                            {showMultiEntity && userData?.companyId && (
                                <MultiEntityUploader
                                    companyId={userData.companyId}
                                    expectedEntities={expectedEntities}
                                    entityNames={entityNames}
                                />
                            )}
                        </>
                    ) : (
                        <UpgradePrompt
                            featureName="Multi-Entity Upload"
                            description="Upload all entity commission files in a single ZIP. VALUTTEN will automatically route each file to the correct office."
                            onUpgrade={() => {
                                // Navigate to billing/upgrade page
                                window.location.href = '/settings/billing';
                            }}
                        />
                    )}
                </div>
            )}

            {/* Original Single File Upload */}
            {!showMultiEntity && (
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg max-w-2xl mx-auto mb-10">

                {error && (
                    <div className="bg-red-900 text-red-300 p-3 rounded mb-4">{error}</div>
                )}
                {message && (
                    <div className="bg-green-900 text-green-300 p-3 rounded mb-4">{message}</div>
                )}
                {processingBanner && (
                    <div className={uploadBannerClass(processingBanner.kind)} role="status" aria-live="polite">
                        {processingBanner.text}
                    </div>
                )}

                <div className="mb-6">
                    <label htmlFor="office-select" className="block mb-2 text-sm font-medium">
                        Select Office:
                    </label>
                    <select 
                        id="office-select" 
                        value={selectedOffice} 
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedOffice(e.target.value)} 
                        className="bg-gray-700 border-gray-600 text-white text-sm rounded-lg block w-full p-2.5"
                    >
                        {offices.map((o: any) => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                    </select>
                </div>
                
                <div className="mb-6">
                    <label htmlFor="template-select" className="block mb-2 text-sm font-medium">
                        Select Loan Book:
                    </label>
                    <select
                        id="template-select"
                        value={selectedTemplate}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTemplate(e.target.value)}
                        className="bg-gray-700 border-gray-600 text-white text-sm rounded-lg block w-full p-2.5"
                    >
                        {(loanBooks.length > 0 ? loanBooks : templates).map((item: any) => (
                            <option key={item.id} value={item.id}>{item.name || item.templateName}</option>
                        ))}
                    </select>
                </div>

                {/* Invoice Date - Only shown for AFG template */}
                {selectedTemplateData?.templateName?.includes('Australian Finance Group') && (
                    <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                            Invoice Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={invoiceDate}
                            onChange={(e) => setInvoiceDate(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="mt-2 text-xs text-gray-400">
                            AFG commission files require an invoice date for processing
                        </p>
                    </div>
                )}

                {/* Commission Run Period Selector */}
                <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-gray-700">
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                        Commission Run Period <span className="text-red-500">*</span>
                    </label>

                    {/* Single-run template info banner */}
                    {isSingleRunTemplate && (
                        <div className="mb-3 p-2 bg-blue-900/30 border border-blue-600 rounded-lg">
                            <p className="text-sm text-blue-400">
                                This aggregator uses a single monthly payment. Run 1 is automatically selected.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-3 gap-4">
                        {/* Run Number */}
                        <div>
                            <label className="block text-xs text-gray-400 mb-2">Run Number</label>
                            <select
                                value={selectedRun}
                                onChange={(e) => setSelectedRun(Number(e.target.value) as 1 | 2 | 3)}
                                disabled={isSingleRunTemplate}
                                className={`w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    isSingleRunTemplate ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                <option value={1}>RUN 1</option>
                                <option value={2}>RUN 2</option>
                                <option value={3}>RUN 3</option>
                            </select>
                        </div>

                        {/* Month */}
                        <div>
                            <label className="block text-xs text-gray-400 mb-2">Month</label>
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Select month...</option>
                                <option value="January">January</option>
                                <option value="February">February</option>
                                <option value="March">March</option>
                                <option value="April">April</option>
                                <option value="May">May</option>
                                <option value="June">June</option>
                                <option value="July">July</option>
                                <option value="August">August</option>
                                <option value="September">September</option>
                                <option value="October">October</option>
                                <option value="November">November</option>
                                <option value="December">December</option>
                            </select>
                        </div>

                        {/* Year */}
                        <div>
                            <label className="block text-xs text-gray-400 mb-2">Year</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <p className="mt-3 text-xs text-gray-400">
                        Generated filename: <span className="text-blue-400 font-mono">
                            RUN {selectedRun} - {selectedMonth || '[Month]'} {selectedYear}.{file?.name?.split('.').pop() || 'xlsx'}
                        </span>
                    </p>
                </div>

                <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-gray-500">
                    <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        onChange={handleFileChange}
                        accept={isPdfStatementTemplate ? '.pdf,application/pdf' : '.csv,.xlsx,.xls'}
                        multiple
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                        <div className="flex flex-col items-center">
                            <CloudUpload className="w-6 h-6" />
                            {file ? (
                                <div className="mt-4 w-full">
                                    <div className="bg-gradient-to-r from-blue-900/40 via-blue-800/60 to-blue-900/40 border-2 border-blue-500 rounded-lg p-4 shadow-lg">
                                        <p className="text-2xl font-bold text-white mb-1">
                                            {file.name}
                                        </p>
                                        <p className="text-sm text-blue-300">
                                            {(file.size / 1024).toFixed(2)} KB
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="mt-2 text-sm text-gray-400">
                                        Click to select file(s)
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {isPdfStatementTemplate ? 'PDF statement' : 'XLSX, XLS, or CSV'}
                                    </p>
                                </>
                            )}
                        </div>
                    </label>
                </div>

                {/* Paired RCTI PDF picker: run/month derivation (Choice Runs) and/or
                    Stage 2 discharge capture (pdfReports-capable templates) */}
                {offersPairedPdf && (
                    <div className="mt-4 border-2 border-dashed border-amber-600/60 rounded-lg p-4 text-center hover:border-amber-500">
                        <input
                            type="file"
                            id="rcti-pdf-upload"
                            className="hidden"
                            accept=".pdf"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) setPdfFile(e.target.files[0]);
                            }}
                        />
                        <label htmlFor="rcti-pdf-upload" className="cursor-pointer">
                            <div className="flex flex-col items-center">
                                <CloudUpload className="w-5 h-5 text-amber-400" />
                                {pdfFile ? (
                                    <p className="mt-2 text-sm font-semibold text-amber-200">{pdfFile.name}</p>
                                ) : (
                                    <>
                                        <p className="mt-2 text-sm text-amber-300">Attach the matching RCTI PDF</p>
                                        <p className="text-xs text-gray-500 mt-1">Required — run &amp; month are read from this PDF</p>
                                    </>
                                )}
                            </div>
                        </label>
                    </div>
                )}

                <button
                    onClick={handleUpload}
                    {...brokerFilteredAdminProps(isBrokerFilteredAdmin)}
                    disabled={isBrokerFilteredAdmin || !file || !selectedMonth || uploading || (isPdfPeriodRange && !pdfFile)}
                    className={`mt-6 w-full font-medium py-3 px-6 rounded-lg transition-colors ${
                        isBrokerFilteredAdmin || !file || !selectedMonth || uploading || (isPdfPeriodRange && !pdfFile)
                            ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                >
                    {uploading ? (
                        <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Uploading...
                        </span>
                    ) : (
                        'Upload Commission File'
                    )}
                </button>
            </div>
            )}

            <div ref={uploadedFilesSectionRef} className="bg-gray-800 p-6 rounded-lg shadow-lg">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">Uploaded Files</h3>
                    {selectedFiles.size > 0 && (
                        <button
                            onClick={() => setShowBatchDeleteConfirm(true)}
                            {...brokerFilteredAdminProps(isBrokerFilteredAdmin)}
                            disabled={isBrokerFilteredAdmin || batchDeleting}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md transition-colors flex items-center gap-2 disabled:bg-red-400"
                        >
                            <Trash2 className="w-5 h-5" />
                            Delete Selected ({selectedFiles.size})
                        </button>
                    )}
                </div>
                {loadingFiles ? (
                    <p>Loading...</p>
                ) : !Object.keys(organizedFiles).length ? (
                    <p className="text-gray-400">No files have been uploaded yet.</p>
                ) : (
                    <div>
                        <div className="flex border-b border-gray-700">
                            {sortedOfficeNames.map((officeName: any) => (
                                <button
                                    key={officeName}
                                    onClick={() => handleTabClick(officeName)}
                                    className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                                        activeOfficeTab === officeName 
                                            ? 'text-blue-500 border-b-2 border-blue-500' 
                                            : 'text-gray-400 border-b-2 border-transparent hover:text-white'
                                    }`}
                                >
                                    {officeName}
                                </button>
                            ))}
                        </div>
                        <div className="mt-4">
                            {activeOfficeTab && organizedFiles[activeOfficeTab] && (
                                <div className="space-y-2">
                                    {Object.entries(organizedFiles[activeOfficeTab]).map(([templateName, years]) => {
                                        const templateKey = `${activeOfficeTab}_${templateName}`;
                                        return (
                                            <div key={templateKey} className="border border-gray-700 rounded-lg">
                                                <button
                                                    onClick={() => toggleTemplate(templateKey)}
                                                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-900 via-blue-900/40 to-slate-900 text-gray-200 rounded-t-lg flex justify-between items-center text-left border-b border-gray-700"
                                                >    
                                                    <span>{templateName}</span>
                                                    <svg 
                                                        className={`w-5 h-5 transition-transform ${
                                                            expandedTemplates.has(templateKey) ? 'rotate-180' : ''
                                                        }`} 
                                                        fill="none" 
                                                        viewBox="0 0 24 24" 
                                                        stroke="currentColor"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                    </svg>
                                                </button>
                                                {expandedTemplates.has(templateKey) && (
                                                    <div className="border-t border-gray-700 p-2 space-y-2">
                                                        {Object.entries(years)
                                                            .sort((a: any, b: any) => b[0].localeCompare(a[0]))
                                                            .map(([year, months]) => {
                                                            const yearKey = `${templateKey}_${year}`;
                                                            const yearFileCount = Object.values(months).flat().length;
                                                            return (
                                                                <div key={yearKey}>
                                                                    <button 
                                                                        onClick={() => toggleYear(yearKey, months)} 
                                                                        className="w-full px-6 py-2 bg-gray-800 flex justify-between items-center text-left rounded-md hover:bg-gray-700"
                                                                    >
                                                                        <span>{year} ({yearFileCount} files)</span>
                                                                        <svg 
                                                                            className={`w-5 h-5 transition-transform ${
                                                                                expandedYears.has(yearKey) ? 'rotate-180' : ''
                                                                            }`} 
                                                                            fill="none" 
                                                                            viewBox="0 0 24 24" 
                                                                            stroke="currentColor"
                                                                        >
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                                        </svg>
                                                                    </button>
                                                                    {expandedYears.has(yearKey) && (
                                                                        <div className="pl-6 pt-2">
                                                                            {Object.entries(months)
                                                                                .sort((a: any, b: any) => b[0].localeCompare(a[0]))
                                                                                .map(([monthKey, files]) => {
                                                                                    const fullMonthKey = `${yearKey}_${monthKey}`;
                                                                                    const monthName = monthKey.split('_')[1];
                                                                                    // Row titles for this month group: displayName, else the
                                                                                    // commission period (plus Run N where several files share
                                                                                    // it), else the filename. Computed per group so the
                                                                                    // shared-period test runs once, not once per row.
                                                                                    const rowTitles = resolveUploadRowTitles(files as UploadRowFile[]);
                                                                                    return (
                                                                                        <div key={fullMonthKey} className="border border-gray-700 rounded-lg overflow-hidden">
                                                                                            <button
                                                                                                onClick={() => toggleMonth(fullMonthKey)}
                                                                                                className="w-full px-4 py-2 flex justify-between items-center text-left text-sm text-gray-200 bg-gray-800/60 border-b border-gray-700"
                                                                                            >
                                                                                                <span>{monthName} ({files.length} files)</span>
                                                                                                <svg 
                                                                                                    className={`w-4 h-4 transition-transform ${
                                                                                                        expandedMonths.has(fullMonthKey) ? 'rotate-180' : ''
                                                                                                    }`} 
                                                                                                    fill="none" 
                                                                                                    viewBox="0 0 24 24" 
                                                                                                    stroke="currentColor"
                                                                                                >
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                                                                </svg>
                                                                                            </button>
                                                                                            {expandedMonths.has(fullMonthKey) && (
                                                                                                <div className="px-2 pb-2">
                                                                                                    <table className="w-full table-fixed text-sm mt-1">
                                                                                                        <thead className="text-xs uppercase bg-gray-700 text-gray-400">
                                                                                                            <tr>
                                                                                                                <th className="w-12 px-4 py-2">
                                                                                                                    <input 
                                                                                                                        type="checkbox" 
                                                                                                                        onChange={() => handleSelectAll(files)} 
                                                                                                                    />
                                                                                                                </th>
                                                                                                                <th className="px-4 py-2 text-left whitespace-nowrap">File Name</th>
                                                                                                                <th className="px-4 py-2 text-left whitespace-nowrap">Upload Date</th>
                                                                                                                <th className="px-4 py-2 text-left whitespace-nowrap">Status</th>
                                                                                                                <th className="w-20 px-4 py-2 text-left">Records</th>
                                                                                                                <th className="w-20 px-4 py-2 text-left">Actions</th>
                                                                                                            </tr>
                                                                                                        </thead>
                                                                                                        <tbody>
                                                                                                            {files.map((file: any) => (
                                                                                                                <React.Fragment key={file.id}>
                                                                                                                <tr className="border-b border-gray-700">
                                                                                                                    <td className="px-4 py-2 text-center">
                                                                                                                        <input
                                                                                                                            type="checkbox"
                                                                                                                            onChange={() => handleFileSelect(file.id)}
                                                                                                                            checked={selectedFiles.has(file.id)}
                                                                                                                        />
                                                                                                                    </td>
                                                                                                                    <td className="px-4 py-2" title={file.fileName}>
                                                                                                                        <button
                                                                                                                            onClick={() => setExpandedFileId(expandedFileId === file.id ? null : file.id)}
                                                                                                                            className="flex w-full min-w-0 items-start gap-1 text-left hover:text-blue-400 transition-colors"
                                                                                                                        >
                                                                                                                            <span className="flex-shrink-0 mt-0.5">
                                                                                                                                {expandedFileId === file.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                                                                                            </span>
                                                                                                                            <span className="min-w-0">
                                                                                                                                <span className="block truncate">{rowTitles.get(file.id)}</span>
                                                                                                                                {rowTitles.get(file.id) !== file.fileName && (
                                                                                                                                    <span className="block truncate text-xs text-gray-400">{file.fileName}</span>
                                                                                                                                )}
                                                                                                                            </span>
                                                                                                                        </button>
                                                                                                                    </td>
                                                                                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                                                                                        {formatDate(file.uploadTimestamp)}
                                                                                                                    </td>
                                                                                                                    <td className="px-4 py-2">
                                                                                                                        {(() => {
                                                                                                                            const effectiveStatus = getEffectiveStatus(file);
                                                                                                                            return (
                                                                                                                                <span className={getStatusBadge(effectiveStatus)} title={
                                                                                                                                    effectiveStatus === 'duplicate'
                                                                                                                                        ? 'This file was already uploaded (duplicate detected by file hash)'
                                                                                                                                        : effectiveStatus === 'empty'
                                                                                                                                        ? 'File processed successfully but contained no new records'
                                                                                                                                        : undefined
                                                                                                                                }>
                                                                                                                                    {effectiveStatus}
                                                                                                                                </span>
                                                                                                                            );
                                                                                                                        })()}
                                                                                                                    </td>
                                                                                                                    <td className="px-4 py-2">
                                                                                                                        {file.recordCount ? file.recordCount :
                                                                                                                            <span className="text-gray-500 text-xs">-</span>
                                                                                                                        }
                                                                                                                    </td>
                                                                                                                    <td className="px-4 py-2">
                                                                                                                        <button
                                                                                                                            onClick={() => {
                                                                                                                                setFileToDelete(file);
                                                                                                                                setShowDeleteConfirm(true);
                                                                                                                            }}
                                                                                                                            {...brokerFilteredAdminProps(isBrokerFilteredAdmin)}
                                                                                                                            className="text-gray-400 hover:text-red-400 disabled:text-gray-600 disabled:hover:text-gray-600 disabled:cursor-not-allowed"
                                                                                                                        >
                                                                                                                            <Trash2 className="w-5 h-5" />
                                                                                                                        </button>
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                                {expandedFileId === file.id && (
                                                                                                                    <tr className="bg-gray-800/50">
                                                                                                                        <td colSpan={6} className="px-8 py-3">
                                                                                                                            <div className="text-xs text-gray-400 space-y-1">
                                                                                                                                <div>Original filename: <span className="text-gray-300 font-mono">{file.originalFileName || file.fileName}</span></div>
                                                                                                                                {file.entityCode && <div>Entity code: <span className="text-gray-300 font-mono">{file.entityCode}</span></div>}
                                                                                                                            </div>
                                                                                                                        </td>
                                                                                                                    </tr>
                                                                                                                )}
                                                                                                                </React.Fragment>
                                                                                                            ))}
                                                                                                        </tbody>
                                                                                                    </table>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showDeleteConfirm && fileToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
                        <p className="text-gray-300">
                            Are you sure you want to delete "{fileToDelete.fileName}"?
                        </p>
                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 bg-gray-600 rounded-md hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                className="px-4 py-2 bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Result Modal - Centered success/error notification */}
            {showResultModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4 border-2 ${
                        resultModalType === 'success' ? 'border-green-500' : 'border-red-500'
                    }`}>
                        <h3 className={`text-lg font-bold mb-4 ${
                            resultModalType === 'success' ? 'text-green-400' : 'text-red-400'
                        }`}>
                            {resultModalType === 'success' ? '✓ Success' : '✗ Error'}
                        </h3>
                        <p className="text-gray-300">{resultModalMessage}</p>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => setShowResultModal(false)}
                                className={`px-6 py-2 rounded-md transition-colors ${
                                    resultModalType === 'success'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBatchDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <h3 className="text-lg font-bold mb-4">Confirm Batch Deletion</h3>
                        <p className="text-gray-300 mb-4">
                            Are you sure you want to delete {selectedFiles.size} selected file(s)?
                        </p>
                        <div className="bg-red-900 bg-opacity-30 border border-red-600 p-3 rounded mb-4">
                            <p className="text-red-400 text-sm">
                                ⚠️ This will permanently delete the commission data from both Firestore and BigQuery.
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowBatchDeleteConfirm(false)}
                                className="px-4 py-2 bg-gray-600 rounded-md hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBatchDeleteConfirm}
                                disabled={batchDeleting}
                                className="px-4 py-2 bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
                            >
                                {batchDeleting ? 'Deleting...' : `Delete ${selectedFiles.size} File(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showMismatchWarning && mismatchDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="text-yellow-500 text-2xl">⚠️</div>
                            <h3 className="text-xl font-bold text-yellow-500">VALUTTEN</h3>
                        </div>

                        <h4 className="text-lg font-semibold mb-4">DATA INTEGRITY WARNING</h4>

                        <div className="space-y-4 text-sm">
                            <div>
                                <p className="text-gray-400 mb-1">Filename suggests:</p>
                                <p className="text-white font-mono bg-gray-700 px-3 py-2 rounded">
                                    {mismatchDetails.originalInfo}
                                </p>
                            </div>

                            <div>
                                <p className="text-gray-400 mb-1">You selected:</p>
                                <p className="text-white font-mono bg-gray-700 px-3 py-2 rounded">
                                    {mismatchDetails.selectedInfo}
                                </p>
                            </div>

                            <div>
                                <p className="text-gray-400 mb-1">Mismatches detected:</p>
                                <ul className="list-disc list-inside text-yellow-400 space-y-1">
                                    {mismatchDetails.mismatches.map((m, i) => (
                                        <li key={i}>{m}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="bg-red-900 bg-opacity-30 border border-red-600 p-3 rounded">
                                <p className="text-red-400 font-semibold">
                                    This will tag {mismatchDetails.originalInfo} data as "{mismatchDetails.selectedInfo}"!
                                </p>
                                <p className="text-red-400 mt-2">
                                    This could cause SERIOUS data corruption.
                                </p>
                            </div>

                            <p className="text-white font-semibold">
                                Are you SURE you want to continue with YOUR selection?
                            </p>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={handleMismatchCancel}
                                className="px-6 py-2 bg-gray-600 rounded-md hover:bg-gray-700 transition-colors font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMismatchOK}
                                className="px-6 py-2 bg-yellow-600 rounded-md hover:bg-yellow-700 transition-colors font-semibold"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Template Mismatch Warning Dialog */}
            {showTemplateMismatch && templateMismatchDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="text-yellow-500 text-2xl">⚠️</div>
                            <h3 className="text-xl font-bold text-yellow-500">VALUTTEN</h3>
                        </div>

                        <h4 className="text-lg font-semibold mb-4">Template Mismatch Warning</h4>

                        <div className="space-y-4 text-sm">
                            <p className="text-gray-300">
                                This file appears to be for <span className="text-white font-semibold">"{templateMismatchDetails.detectedTemplate}"</span> based on its filename pattern,
                                but you have <span className="text-white font-semibold">"{templateMismatchDetails.selectedTemplate}"</span> selected.
                            </p>

                            <div className="bg-gray-700 px-3 py-2 rounded">
                                <p className="text-gray-400 text-xs mb-1">Filename:</p>
                                <p className="text-white font-mono text-xs break-all">{templateMismatchDetails.filename}</p>
                            </div>

                            <div className="bg-red-900 bg-opacity-30 border border-red-600 p-3 rounded">
                                <p className="text-red-400">
                                    Uploading with the wrong template will cause processing to fail.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3 mt-6">
                            <button
                                onClick={handleTemplateMismatchSwitch}
                                className="w-full px-6 py-3 bg-blue-600 rounded-md hover:bg-blue-700 transition-colors font-semibold"
                            >
                                Switch to "{templateMismatchDetails.detectedTemplate}" and upload
                            </button>
                            <button
                                onClick={handleTemplateMismatchProceed}
                                className="w-full px-6 py-2 bg-gray-600 rounded-md hover:bg-gray-700 transition-colors text-gray-300"
                            >
                                Proceed with "{templateMismatchDetails.selectedTemplate}" anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default UploadPage;